import { useState, useEffect } from 'react'
import { supabase, supabaseUrl, supabaseAnonKey } from '../supabaseClient'
import { useNavigate, Link, useLocation } from 'react-router-dom'

export default function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [serverStatus, setServerStatus] = useState('checking') // checking, online, offline
  const [rememberMe, setRememberMe] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  // Load saved credentials on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('rememberedCredentials')
    if (savedCredentials) {
      try {
        const { email, password: savedPassword } = JSON.parse(savedCredentials)
        setEmailOrUsername(email || '')
        setPassword(savedPassword || '')
        setRememberMe(true)
      } catch (e) {
        console.error('Failed to parse saved credentials:', e)
        localStorage.removeItem('rememberedCredentials')
      }
    }
  }, [])

  // Check server status on mount
  useEffect(() => {
    const checkServerStatus = async () => {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 8000) // Increase timeout to 8s
        
        const response = await fetch(`${supabaseUrl}/auth/v1/health`, {
          signal: controller.signal,
          headers: { 'apikey': supabaseAnonKey }
        })
        
        clearTimeout(timeoutId)
        setServerStatus(response.ok ? 'online' : 'checking') // Don't immediately mark offline
      } catch (err) {
        console.error('Server check failed:', err)
        // Only mark offline after multiple failed attempts
        setServerStatus('checking')
      }
    }
    
    checkServerStatus()
  }, [])

  // Show message from registration or sessionStorage (after failed login)
  useEffect(() => {
    if (location.state?.message) {
      setMessage({ type: 'info', text: location.state.message })
      // Clear the state after a delay to ensure message is shown
      setTimeout(() => {
        window.history.replaceState({}, document.title)
      }, 100)
    } else {
      // Check for pending/rejected message in sessionStorage (only after login attempt)
      const savedMessage = sessionStorage.getItem('loginMessage')
      if (savedMessage) {
        try {
          setMessage(JSON.parse(savedMessage))
          sessionStorage.removeItem('loginMessage') // Show once, then remove
        } catch (e) {
          console.error('Failed to parse saved message:', e)
          sessionStorage.removeItem('loginMessage')
        }
      }
    }
  }, [location])

  const handleLogin = async (e) => {
    e.preventDefault()
    setMessage(null)
    setLoading(true)

    try {
      // Check if online
      if (!navigator.onLine) {
        setMessage({ type: 'error', text: '❌ Tidak ada koneksi internet. Periksa koneksi Anda.' })
        setLoading(false)
        return
      }

      // Check if input is email or username
      let loginEmail = emailOrUsername
      const isEmail = emailOrUsername.includes('@')
      
      // If not email, lookup username in database
      if (!isEmail) {
        
        try {
          // Use RPC call to bypass RLS policy issues
          const { data: userData, error: lookupError } = await supabase
            .rpc('get_email_by_username', { 
              username_input: emailOrUsername.toLowerCase() 
            })
          
          
          if (lookupError) {
            console.error('Username lookup error:', lookupError)
            
            // Fallback: try direct query with service role
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('users')
              .select('email')
              .eq('username', emailOrUsername.toLowerCase())
              .single()
            
            if (fallbackError || !fallbackData) {
              setMessage({ 
                type: 'error', 
                text: `❌ Username "${emailOrUsername}" tidak ditemukan. Gunakan email untuk login.` 
              })
              setLoading(false)
              return
            }
            
            loginEmail = fallbackData.email
          } else if (userData) {
            loginEmail = userData
          } else {
            setMessage({ 
              type: 'error', 
              text: `❌ Username "${emailOrUsername}" tidak ditemukan. Gunakan email untuk login.` 
            })
            setLoading(false)
            return
          }
        } catch (err) {
          console.error('Lookup exception:', err)
          setMessage({ 
            type: 'error', 
            text: '❌ Terjadi kesalahan. Silakan gunakan email untuk login.' 
          })
          setLoading(false)
          return
        }
        
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: loginEmail, 
        password 
      })

      if (error) {
        // Handle specific error types
        if (error.message?.includes('fetch') || error.message?.includes('Failed to fetch')) {
          setMessage({ 
            type: 'error', 
            text: '🌐 Tidak dapat terhubung ke server. Server mungkin sedang maintenance. Coba lagi dalam beberapa menit.' 
          })
        } else if (error.message?.includes('Invalid login credentials')) {
          setMessage({ type: 'error', text: '❌ Email atau password salah.' })
        } else if (error.message?.includes('Email not confirmed')) {
          setMessage({ type: 'error', text: '⚠️ Email belum dikonfirmasi. Cek inbox Anda.' })
        } else {
          setMessage({ type: 'error', text: error.message })
        }
        setLoading(false)
      } else {
        // Check user status from database
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('status, full_name')
          .eq('id', data.user.id)
          .single()

        if (userError) {
          console.error('Failed to fetch user status:', userError)
          setMessage({ 
            type: 'error', 
            text: '⚠️ Gagal memuat data pengguna. Silakan coba lagi.' 
          })
          await supabase.auth.signOut()
          setLoading(false)
          return
        }

        // Check if user is approved
        if (userData.status === 'pending') {
          await supabase.auth.signOut()
          const pendingMessage = {
            type: 'info',
            text: '⏳ Akun Anda masih menunggu verifikasi dari administrator. Mohon tunggu hingga akun Anda disetujui untuk dapat mengakses sistem. Terima kasih atas kesabaran Anda.'
          }
          sessionStorage.setItem('loginMessage', JSON.stringify(pendingMessage))
          setMessage(pendingMessage)
          setLoading(false)
          return
        } else if (userData.status === 'rejected') {
          await supabase.auth.signOut()
          const rejectedMessage = {
            type: 'error',
            text: '❌ Akun Anda ditolak oleh administrator. Silakan hubungi admin untuk informasi lebih lanjut.'
          }
          sessionStorage.setItem('loginMessage', JSON.stringify(rejectedMessage))
          setMessage(rejectedMessage)
          setLoading(false)
          return
        }

        // Record sign in for approved users
        if (data?.user?.id) {
          try {
            await supabase.rpc('record_user_sign_in', { user_id: data.user.id })
          } catch (err) {
            console.error('Failed to record sign in:', err)
            // Continue anyway
          }
        }

        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('rememberedCredentials', JSON.stringify({
            email: emailOrUsername,
            password: password
          }))
        } else {
          localStorage.removeItem('rememberedCredentials')
        }
        
        // Redirect to dashboard - user is approved
        sessionStorage.removeItem('loginMessage') // Clear any pending/rejected message
        setMessage({ type: 'success', text: '✅ Login berhasil! Mengarahkan...' })
        setTimeout(() => navigate('/dashboard'), 1500)
        setLoading(false)
      }
    } catch (err) {
      console.error('Login error:', err)
      setMessage({ 
        type: 'error', 
        text: '⚠️ Terjadi kesalahan. Server mungkin sedang maintenance. Silakan coba lagi nanti.' 
      })
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white flex flex-col">
      {/* Server Status Banner */}
      {serverStatus === 'offline' && (
        <div className="relative z-50 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white px-4 py-3 text-center text-sm font-medium shadow-lg">
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>⚠️ Server sedang mengalami gangguan. Mohon coba lagi dalam beberapa menit.</span>
          </div>
        </div>
      )}

      <div className="flex flex-1">
      {/* Left Side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/assets/backgrounds/login-background-alt.png')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        >
          {/* Dark Overlay for better contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-blue-900/40 to-blue-900/40"></div>
        </div>

        {/* Floating Particles on Image Side */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-3 h-3 bg-white/40 rounded-full animate-float shadow-lg"></div>
          <div className="absolute top-40 right-20 w-4 h-4 bg-blue-300/40 rounded-full animate-float-delayed shadow-lg"></div>
          <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-blue-300/40 rounded-full animate-float-slow shadow-lg"></div>
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue-300/40 rounded-full animate-float shadow-lg"></div>
          <div className="absolute bottom-20 right-10 w-2 h-2 bg-cyan-300/40 rounded-full animate-float-delayed shadow-lg"></div>
          <div className="absolute top-1/2 left-20 w-4 h-4 bg-pink-300/40 rounded-full animate-float-slow shadow-lg"></div>
          <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-blue-400/40 rounded-full animate-float shadow-lg"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-white">
            <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">Welcome Back!</h1>
            <p className="text-xl text-white/90 drop-shadow-md">Sign in to continue your journey</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 relative overflow-hidden">
        {/* Futuristic Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Orbs */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/40 via-blue-200/40 to-pink-200/40 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-200/40 via-blue-200/40 to-cyan-200/40 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-slow"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>

          {/* Floating Particles on Form Side */}
          <div className="absolute top-20 left-10 w-2 h-2 bg-blue-400/30 rounded-full animate-float"></div>
          <div className="absolute top-40 right-20 w-3 h-3 bg-blue-400/30 rounded-full animate-float-delayed"></div>
          <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-pink-400/30 rounded-full animate-float-slow"></div>
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue-400/30 rounded-full animate-float"></div>
          <div className="absolute bottom-20 right-10 w-2 h-2 bg-cyan-400/30 rounded-full animate-float-delayed"></div>
        </div>

      {/* Login Card Container */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Glass Morphism Card */}
          <div className="relative backdrop-blur-xl bg-white/70 p-10 rounded-3xl shadow-2xl border border-white/40">
            {/* Shimmer Effect on Card Border */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer pointer-events-none"></div>
            
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-400 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                <img 
                  src="/assets/logos/logo-variant-4.png" 
                  alt="Logo" 
                  className="relative h-32 w-auto object-contain drop-shadow-lg"
                />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-blue-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Selamat Datang
              </h2>
              <p className="text-slate-500 text-sm">Masuk untuk melanjutkan ke dashboard</p>
            </div>

            {/* Message Alert */}
            {message && (
              <div
                className={`mb-6 px-4 py-4 rounded-xl text-sm font-medium transition-all duration-300 backdrop-blur-sm ${
                  message.type === 'success'
                    ? 'bg-green-100/80 text-green-700 border border-green-200/50 shadow-lg shadow-green-100/50'
                    : message.type === 'info'
                    ? 'bg-amber-100/80 text-amber-800 border border-amber-200/50 shadow-lg shadow-amber-100/50'
                    : 'bg-red-100/80 text-red-700 border border-red-200/50 shadow-lg shadow-red-100/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {message.type === 'info' && (
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className="flex-1">{message.text}</span>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username/Email Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-blue-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Username atau Email"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    required
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-pink-400 rounded-xl opacity-0 group-focus-within:opacity-30 blur transition duration-300"></div>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="relative w-full bg-white/50 backdrop-blur-sm border border-slate-200 pl-12 pr-32 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 hover:text-blue-600 font-medium cursor-pointer select-none transition-colors duration-300"
                  >
                    {showPassword ? 'Sembunyikan' : 'Tampilkan'}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border-2 border-slate-300 rounded-md bg-white/50 peer-checked:bg-gradient-to-r peer-checked:from-blue-500 peer-checked:to-blue-500 peer-checked:border-transparent transition-all duration-200 flex items-center justify-center">
                      <svg 
                        className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <svg 
                      className={`absolute inset-0 w-5 h-5 text-white transition-opacity duration-200 ${rememberMe ? 'opacity-100' : 'opacity-0'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors select-none">
                    Ingat Saya
                  </span>
                </label>
              </div>

              {/* Info: Password Reset by Admin */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Lupa password? Hubungi <span className="font-semibold text-blue-600">Administrator</span> untuk reset password
                </p>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-600 to-pink-600 rounded-xl transition-all duration-300 group-hover:shadow-xl group-hover:shadow-blue-500/50"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-600 to-pink-600 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300"></div>
                <div className="relative px-6 py-3.5 text-white font-semibold flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Memproses...</span>
                    </>
                  ) : (
                    <>
                      <span>Masuk</span>
                      <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </div>
              </button>
            </form>

            {/* Register Link */}
            <div className="text-center mt-8">
              <p className="text-sm text-slate-600">
                Belum punya akun?{' '}
                <Link
                  to="/register"
                  className="font-semibold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent hover:from-blue-600 hover:to-pink-600 transition-all duration-300"
                >
                  Daftar Sekarang
                </Link>
              </p>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-blue-400/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-pink-400/20 to-blue-400/20 rounded-full blur-2xl"></div>
          </div>

          {/* Footer Text */}
          <div className="text-center mt-8">
            <p className="text-xs text-slate-400">
              © 2024 • Sistem Manajemen Karyawan • Secure & Reliable
            </p>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>
  )
}
